import { ServerInferRequest } from "@ts-rest/core";
import {
    InfiniteData,
    QueryClient,
    useMutation,
    useQuery,
    useQueryClient,
} from "@tanstack/react-query";
import { financialDocumentContract } from "@common/contracts/financial-document.contract";
import { FinancialDocumentExtendedDto } from "@common/dto/financialDocument/financial-document-extended.dto";
import {
    DocumentTaxonomyExtraction,
    FinancialDocument,
    FinancialDocumentExtended,
    FinancialDocumentListItem,
    ImporterErrors,
    isFinancialDocumentExtended,
} from "@common/types/financialDocument";
import { projectFinancialDocumentListItem } from "@components/documents/projectFinancialDocumentListItem";
import { BalanceSheetAccount } from "@common/dto/financialDocument/balance-sheet-extraction.schema";
import { AccrualExtractionLineItem } from "@common/dto/financialDocument/accrual-document-extraction.schema";
import { BillExtractionRecord } from "@common/schemas/financialDocument/bill-extraction-record.schema";
import { InvoiceExtractionRecord } from "@common/schemas/financialDocument/invoice-extraction-record.schema";
import { AccountInfoExtraction } from "@common/schemas/financialDocument/account-info-extraction.schema";
import { FinancialDocumentDetailIncludeQuery } from "@common/schemas/financialDocument/financial-document-detail-include.schema";
import {
    financialDocumentQueryKeys,
    financialDocumentsForFinancialAccountKeys,
    updateFinancialDocumentDetailCaches,
    patchFinancialDocumentDetailCaches,
} from "@components/documents/lib";
import { FetchFinancialDocumentsResponse } from "@common/dto/financialDocument/fetch-financial-documents-response.dto";
import { useScope } from "@state/scopeContext.lib";
import { useBillingStatus } from "@hooks/useBillingStatus";
import { invalidateAccountRelatedQueries } from "@api/account.api";
import { invalidateTransactionDetailsQueries } from "@components/transactions/TransactionDetails/useTransactionDetailsQuery";
import { isAxiosError } from "axios";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { InvalidDocumentCategoryError } from "@common/schemas/financialDocument/invalid-document-category-error.schema";
import { LinkedDocumentFieldUpdateError } from "@common/schemas/financialDocument/linked-document-field-update-error.schema";
import { RemovingDocumentWithTransactionsError } from "@common/schemas/financialDocument/removing-document-with-transactions-error.schema";
import { RemovingDocumentUsedInReconciliationError } from "@common/schemas/financialDocument/removing-document-used-in-reconciliation-error.schema";
import { FinancialDocumentDto } from "@common/dto/financialDocument/financial-document.dto";
import {
    fetchFinancialDocument,
    runTaxonomyExtraction,
} from "@lib/financialDocument";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { chunk } from "@common/helpers/collections";
import { ErrorMessageSchema } from "@common/schemas/error.schema";
import { AccrualDocumentImporterMode } from "@components/accrualDocumentImporter/accrualDocumentImporter.lib";
import { tasksQueryKeys } from "./task.api";
import { invalidateAllTaskQueries } from "./taskQueryKeys";
import { invalidateInvoiceQueries } from "./invoice.api";
import { invalidateBillQueries } from "./bill.api";

const financialDocumentContractClient = getBackendAPIClient(
    financialDocumentContract,
);

interface AccrualExtractionRequestResult {
    status: number;
    body?: object | null;
}

function getErrorMessageFromResponseBody(
    body: object | null | undefined,
): string | null {
    const parsed = ErrorMessageSchema.safeParse(body);
    if (parsed.success) {
        return parsed.data.message;
    }

    return null;
}

async function executeAccrualExtractionRequest(
    request: () => Promise<AccrualExtractionRequestResult>,
    fallbackMessage: string,
): Promise<void> {
    try {
        const result = await request();
        if (result.status === 200) {
            return;
        }

        const message = getErrorMessageFromResponseBody(result.body);
        throw new Error(message ?? fallbackMessage);
    } catch (caught) {
        if (isAxiosError(caught) && caught.response?.status === 400) {
            const message = getErrorMessageFromResponseBody(
                typeof caught.response.data === "object"
                    ? caught.response.data
                    : null,
            );
            if (message) {
                throw new Error(message);
            }
        }

        if (caught instanceof Error) {
            throw caught;
        }

        throw new Error(fallbackMessage);
    }
}

export function useFinancialDocument(
    financialDocumentId?: number,
    detailIncludes?: FinancialDocumentDetailIncludeQuery,
) {
    const resolvedDetailIncludes = detailIncludes ?? {};
    return useQuery({
        queryKey: financialDocumentQueryKeys.detail(
            financialDocumentId ?? 0,
            resolvedDetailIncludes,
        ),
        queryFn: () =>
            fetchFinancialDocument(
                financialDocumentId ?? 0,
                resolvedDetailIncludes,
            ),
        enabled: !!financialDocumentId,
    });
}

export function useCreateUploadUrlMutation() {
    return useMutation({
        mutationFn: async (
            body: ServerInferRequest<
                typeof financialDocumentContract.createUploadUrl
            >["body"],
        ) => {
            const result =
                await financialDocumentContractClient.createUploadUrl({ body });

            return result.body.url;
        },
    });
}

export function useProcessS3Upload(workspaceId: string) {
    return useMutation({
        mutationFn: async (
            body: ServerInferRequest<
                typeof financialDocumentContract.uploadDocumentS3
            >["body"],
        ) => {
            const result =
                await financialDocumentContractClient.uploadDocumentS3({
                    body: {
                        ...body,
                        workspaceId,
                    },
                });

            return result.body.map(FinancialDocumentExtendedDto.deserialize);
        },
    });
}

type UpdateDocumentMetadataBody = ServerInferRequest<
    typeof financialDocumentContract.updateDocumentMetadata
>["body"];

const metadataPatchFields = [
    "openingBalance",
    "openingDate",
    "closingBalance",
    "closingDate",
    "fileName",
    "financialAccountId",
    "entityId",
] as const satisfies ReadonlyArray<
    keyof UpdateDocumentMetadataBody & keyof FinancialDocument
>;

function metadataRollbackPatch(
    previousDocument: FinancialDocument,
    body: UpdateDocumentMetadataBody,
): Partial<FinancialDocument> {
    const patch: Partial<FinancialDocument> = {};

    const assignPreviousValueIfPresent = <
        Field extends keyof UpdateDocumentMetadataBody &
            keyof FinancialDocument,
    >(
        field: Field,
    ) => {
        if (field in body) {
            patch[field] = previousDocument[field];
        }
    };

    for (const field of metadataPatchFields) {
        assignPreviousValueIfPresent(field);
    }

    return patch;
}

export function useUpdateFinancialDocumentMetadataMutation(
    financialDocument: FinancialDocument,
) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (
            body: ServerInferRequest<
                typeof financialDocumentContract.updateDocumentMetadata
            >["body"],
        ) => {
            const result =
                await financialDocumentContractClient.updateDocumentMetadata({
                    body,
                    params: {
                        financialDocumentId: financialDocument.id,
                    },
                });

            return FinancialDocumentDto.deserialize(result.body);
        },
        onMutate: async (body) => {
            await queryClient.cancelQueries({
                queryKey: financialDocumentQueryKeys.details(),
            });
            patchDocumentInAllCaches(queryClient, financialDocument.id, body);

            return financialDocument;
        },
        onSuccess: (updatedDocument) => {
            updateDocumentOnLists(updatedDocument, queryClient);
            patchFinancialDocumentDetailCaches(
                queryClient,
                updatedDocument.id,
                updatedDocument,
            );
        },
        onError: (_, body, previousDocument) => {
            if (previousDocument) {
                const rollbackPatch = metadataRollbackPatch(
                    previousDocument,
                    body,
                );
                patchDocumentInAllCaches(
                    queryClient,
                    previousDocument.id,
                    rollbackPatch,
                );
            }
        },
    });
}

interface AddFinancialDocumentToImporterOptions {
    financialDocument: FinancialDocumentListItem;
    importerQueue?: string;
    reconciliedFinancialAccountId?: number;
    forceRerun?: boolean;
    onSuccess?: (updatedDocument: FinancialDocumentExtended) => void;
}

export function useAddFinancialDocumentToImporterMutation(
    options: AddFinancialDocumentToImporterOptions,
) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => {
            const response =
                await financialDocumentContractClient.addToImporter({
                    params: {
                        financialDocumentId: options.financialDocument.id,
                    },
                    body: {
                        importerQueue: options.importerQueue,
                        reconciliedFinancialAccountId:
                            options.reconciliedFinancialAccountId,
                        ...(options.forceRerun && { forceRerun: true }),
                    },
                });

            return FinancialDocumentExtendedDto.deserialize(response.body);
        },
        onSuccess: (updatedDocument) => {
            updateDocumentInCaches(updatedDocument, queryClient);
            queryClient.invalidateQueries({
                queryKey: financialDocumentQueryKeys.lists(),
            });
            options.onSuccess?.(updatedDocument);
        },
    });
}

const ADD_TO_IMPORTER_BATCH_SIZE = 5;

interface AddFinancialDocumentsToImporterResult {
    updatedDocuments: FinancialDocumentExtended[];
    failedCount: number;
}

interface AddFinancialDocumentsToImporterOptions {
    importerQueue?: string;
    reconciliedFinancialAccountId?: number;
    forceRerun?: boolean;
    onSettled?: (result: AddFinancialDocumentsToImporterResult) => void;
}

export function useAddFinancialDocumentsToImporterMutation(
    options: AddFinancialDocumentsToImporterOptions,
) {
    const queryClient = useQueryClient();

    return useMutation({
        // Add each document independently so a single failure (network, rate
        // limit, transient 5xx) doesn't discard the documents that succeeded.
        // Requests run in bounded batches to avoid hammering the API with a
        // large selection (the use case is ~100 docs).
        mutationFn: async (
            financialDocuments: Array<{ id: number }>,
        ): Promise<AddFinancialDocumentsToImporterResult> => {
            const updatedDocuments: FinancialDocumentExtended[] = [];
            let failedCount = 0;

            for (const batch of chunk(
                financialDocuments,
                ADD_TO_IMPORTER_BATCH_SIZE,
            )) {
                const settledResults = await Promise.allSettled(
                    batch.map(async (financialDocument) => {
                        const response =
                            await financialDocumentContractClient.addToImporter(
                                {
                                    params: {
                                        financialDocumentId:
                                            financialDocument.id,
                                    },
                                    body: {
                                        importerQueue: options.importerQueue,
                                        reconciliedFinancialAccountId:
                                            options.reconciliedFinancialAccountId,
                                        ...(options.forceRerun && {
                                            forceRerun: true,
                                        }),
                                    },
                                },
                            );

                        return FinancialDocumentExtendedDto.deserialize(
                            response.body,
                        );
                    }),
                );

                settledResults.forEach((settledResult) => {
                    if (settledResult.status === "fulfilled") {
                        updatedDocuments.push(settledResult.value);
                    } else {
                        failedCount += 1;
                    }
                });
            }

            return { updatedDocuments, failedCount };
        },
        onSuccess: ({ updatedDocuments, failedCount }) => {
            updatedDocuments.forEach((updatedDocument) =>
                updateDocumentInCaches(updatedDocument, queryClient),
            );
            queryClient.invalidateQueries({
                queryKey: financialDocumentQueryKeys.lists(),
            });
            options.onSettled?.({ updatedDocuments, failedCount });
        },
    });
}

export function useRemoveImporterErrorMutation(
    document: FinancialDocumentExtended,
) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (error: ImporterErrors) => {
            const result =
                await financialDocumentContractClient.removeImporterError({
                    params: {
                        financialDocumentId: document.id,
                    },
                    body: {
                        toRemove: error,
                    },
                });

            return FinancialDocumentExtendedDto.deserialize(result.body);
        },
        onMutate: async (removedError) => {
            await queryClient.cancelQueries({
                queryKey: financialDocumentQueryKeys.details(),
            });
            const importerErrors =
                document.importerErrors?.filter((e) => e !== removedError) ??
                null;

            patchDocumentInAllCaches(queryClient, document.id, {
                importerErrors,
            });
            return document;
        },
        onSuccess: (updatedDocument) => {
            updateDocumentInCaches(updatedDocument, queryClient);
        },
        onError: (_, __, previousDocument) => {
            if (previousDocument) {
                const rollbackPatch = {
                    importerErrors: previousDocument.importerErrors,
                };
                patchDocumentInAllCaches(
                    queryClient,
                    previousDocument.id,
                    rollbackPatch,
                );
            }
        },
    });
}

export function useFinancialDocumentFinalizeTransactionsImportMutation() {
    const queryClient = useQueryClient();
    const { workspaceId } = useScope();

    return useMutation({
        mutationFn: async (financialDocumentIds: number[]) => {
            if (!workspaceId) {
                throw new Error("No workspace selected");
            }

            await financialDocumentContractClient.finalizeTransactionsImport({
                body: {
                    workspaceId,
                    financialDocumentIds,
                },
            });
        },
        onSuccess: async () => {
            invalidateAccountRelatedQueries({ workspaceId });
            await queryClient.invalidateQueries({
                queryKey: financialDocumentQueryKeys.lists(),
            });
            await queryClient.invalidateQueries({
                queryKey: financialDocumentQueryKeys.details(),
            });
        },
    });
}

export function useFinancialDocumentFinalizeAccrualImportMutation(
    mode: AccrualDocumentImporterMode,
) {
    const queryClient = useQueryClient();
    const { workspaceId } = useScope();

    return useMutation({
        mutationFn: async (financialDocumentIds: number[]) => {
            if (!workspaceId) {
                throw new Error("No workspace selected");
            }

            const body = { workspaceId, financialDocumentIds };

            if (mode === "invoices") {
                await financialDocumentContractClient.finalizeInvoicesImport({
                    body,
                });
                return;
            }

            await financialDocumentContractClient.finalizeBillsImport({
                body,
            });
        },
        onSuccess: async () => {
            queryClient.invalidateQueries({
                queryKey: financialDocumentQueryKeys.lists(),
            });

            if (mode === "invoices") {
                invalidateInvoiceQueries();
                return;
            }

            invalidateBillQueries();
        },
    });
}

export function useConfirmAccrualCounterpartiesMutation() {
    const queryClient = useQueryClient();
    const { workspaceId } = useScope();

    return useMutation({
        mutationFn: async (financialDocumentIds: number[]) => {
            if (!workspaceId) {
                throw new Error("No workspace selected");
            }
            const response =
                await financialDocumentContractClient.confirmAccrualCounterparties(
                    { body: { workspaceId, financialDocumentIds } },
                );
            return response.body.confirmedCount;
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: financialDocumentQueryKeys.lists(),
            });
            await queryClient.invalidateQueries({
                queryKey: financialDocumentQueryKeys.details(),
            });
        },
    });
}

export interface CheckForImporterErrorsOptions {
    financialDocumentId: number;
    checkTransactions?: boolean;
}

interface CheckForImporterErrorsContext {
    requestId: number;
    financialDocumentId: number;
}

let checkForImporterErrorsRequestCounter = 0;
const latestCheckForImporterErrorsRequestByDocumentId = new Map<
    number,
    number
>();

function clearLatestCheckForImporterErrorsRequestIfCurrent({
    financialDocumentId,
    requestId,
}: CheckForImporterErrorsContext) {
    if (
        latestCheckForImporterErrorsRequestByDocumentId.get(
            financialDocumentId,
        ) === requestId
    ) {
        latestCheckForImporterErrorsRequestByDocumentId.delete(
            financialDocumentId,
        );
    }
}

export function useCheckForImporterErrors() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (options: CheckForImporterErrorsOptions) => {
            const { body } =
                await financialDocumentContractClient.checkForImporterErrors({
                    params: {
                        financialDocumentId: options.financialDocumentId,
                    },
                    query: {
                        checkTransactions: options.checkTransactions,
                    },
                });

            return FinancialDocumentExtendedDto.deserialize(body);
        },
        onMutate: (options) => {
            const requestId = ++checkForImporterErrorsRequestCounter;
            latestCheckForImporterErrorsRequestByDocumentId.set(
                options.financialDocumentId,
                requestId,
            );

            return {
                requestId,
                financialDocumentId: options.financialDocumentId,
            } satisfies CheckForImporterErrorsContext;
        },
        onSuccess: (updatedDocument, options, context) => {
            if (
                !context ||
                latestCheckForImporterErrorsRequestByDocumentId.get(
                    options.financialDocumentId,
                ) !== context.requestId
            ) {
                return;
            }

            updateDocumentInCaches(updatedDocument, queryClient);
            clearLatestCheckForImporterErrorsRequestIfCurrent(context);
        },
        onError: (_error, _options, context) => {
            if (context) {
                clearLatestCheckForImporterErrorsRequestIfCurrent(context);
            }
        },
    });
}

function patchDocumentInAllCaches(
    queryClient: QueryClient,
    documentId: number,
    patch: Partial<FinancialDocument>,
) {
    mapDocumentOnLists(queryClient, (document) =>
        document.id === documentId ? { ...document, ...patch } : document,
    );
    patchFinancialDocumentDetailCaches(queryClient, documentId, patch);
}

function mapDocumentOnLists(
    queryClient: QueryClient,
    mapper: (doc: FinancialDocumentListItem) => FinancialDocumentListItem,
) {
    queryClient.setQueriesData(
        { queryKey: financialDocumentQueryKeys.lists() },
        (
            oldData:
                | FetchFinancialDocumentsResponse
                | InfiniteData<FetchFinancialDocumentsResponse>
                | undefined,
        ) => {
            if (!oldData) {
                return oldData;
            }

            if ("pages" in oldData) {
                return {
                    ...oldData,
                    pages: oldData.pages.map((page) => ({
                        ...page,
                        data: page.data.map(mapper),
                    })),
                };
            }

            return {
                ...oldData,
                data: oldData.data.map(mapper),
            };
        },
    );
}

function updateDocumentOnLists(
    updatedDocument: FinancialDocument | FinancialDocumentExtended,
    queryClient: QueryClient,
) {
    const listPatch = isFinancialDocumentExtended(updatedDocument)
        ? projectFinancialDocumentListItem(updatedDocument)
        : updatedDocument;

    mapDocumentOnLists(queryClient, (document) =>
        document.id === updatedDocument.id
            ? { ...document, ...listPatch }
            : document,
    );
}

function updateDocumentInCaches(
    updatedDocument: FinancialDocument | FinancialDocumentExtended,
    queryClient: QueryClient,
) {
    updateDocumentOnLists(updatedDocument, queryClient);

    if (isFinancialDocumentExtended(updatedDocument)) {
        patchFinancialDocumentDetailCaches(
            queryClient,
            updatedDocument.id,
            updatedDocument,
        );
    }
}

export function useRemoveFinancialDocumentFromImporterMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (financialDocument: FinancialDocumentExtended) =>
            financialDocumentContractClient.removeFromImporter({
                params: {
                    financialDocumentId: financialDocument.id,
                },
            }),
        onMutate: (financialDocument) => {
            const importerQueue = financialDocument.importerQueue;
            if (importerQueue) {
                queryClient.cancelQueries({
                    queryKey:
                        financialDocumentQueryKeys.importerQueue(importerQueue),
                    exact: false,
                });
                queryClient.setQueriesData(
                    {
                        queryKey:
                            financialDocumentQueryKeys.importerQueue(
                                importerQueue,
                            ),
                    },
                    (oldData: FetchFinancialDocumentsResponse | undefined) => {
                        if (!oldData) {
                            return oldData;
                        }
                        return {
                            ...oldData,
                            data: oldData.data.filter(
                                (d) => d.id !== financialDocument.id,
                            ),
                        };
                    },
                );
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: financialDocumentQueryKeys.lists(),
            });
        },
        onError: (_error, financialDocument) => {
            const importerQueue = financialDocument.importerQueue;
            if (importerQueue) {
                queryClient.invalidateQueries({
                    queryKey:
                        financialDocumentQueryKeys.importerQueue(importerQueue),
                });
            }
        },
    });
}

interface UseRemoveFinancialDocumentMutationOptions {
    onError?: (
        error:
            | Error
            | RemovingDocumentWithTransactionsError
            | RemovingDocumentUsedInReconciliationError,
    ) => void;
}

interface UseRemoveFinancialDocumentMutationParams {
    financialDocumentId: number;
    confirmTransactionsRemoval?: boolean;
    optimisticUpdate?: boolean;
}

export function useRemoveFinancialDocumentMutation(
    options: UseRemoveFinancialDocumentMutationOptions = {},
) {
    const { invalidate: invalidateCreditsUsed } = useBillingStatus();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            financialDocumentId,
            confirmTransactionsRemoval,
        }: UseRemoveFinancialDocumentMutationParams) =>
            financialDocumentContractClient.deleteDocument({
                params: {
                    financialDocumentId,
                },
                query: {
                    confirmTransactionsRemoval,
                },
            }),
        onMutate({ financialDocumentId, optimisticUpdate = true }) {
            if (!optimisticUpdate) {
                return;
            }
            queryClient.setQueriesData<FetchFinancialDocumentsResponse>(
                { queryKey: financialDocumentQueryKeys.lists() },
                (oldData) => {
                    if (!oldData?.data) {
                        return;
                    }

                    return {
                        ...oldData,
                        data: oldData.data.filter(
                            (document) => document.id !== financialDocumentId,
                        ),
                        total:
                            oldData.total !== undefined
                                ? oldData.total - 1
                                : undefined,
                    };
                },
            );
        },
        onError: (error) => {
            if (
                isAxiosError(error) &&
                error.response?.status === 400 &&
                error.response?.data
            ) {
                options.onError?.(
                    error.response
                        .data as RemovingDocumentWithTransactionsError,
                );
            } else {
                options.onError?.(error);
            }
        },
        async onSettled() {
            await queryClient.invalidateQueries({
                queryKey: financialDocumentQueryKeys.lists(),
            });

            await invalidateCreditsUsed();
            await invalidateTransactionDetailsQueries();

            await queryClient.invalidateQueries({
                queryKey: financialDocumentsForFinancialAccountKeys.all(),
            });
            await invalidateAllTaskQueries(queryClient);
        },
    });
}

export type AccountInfoUpdateBody = ServerInferRequest<
    typeof financialDocumentContract.updateAccountInfo
>["body"];

interface UseAccountInfoUpdateMutationParams<TContext = void> {
    financialDocumentId: number;
    onMutate?: (body: AccountInfoUpdateBody) => TContext;
    onSuccess?: (updatedExtraction: AccountInfoExtraction) => void;
    onError?: (context: TContext | undefined) => void;
}

export function useAccountInfoUpdateMutation<TContext = void>({
    financialDocumentId,
    onMutate,
    onSuccess,
    onError,
}: UseAccountInfoUpdateMutationParams<TContext>) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (body: AccountInfoUpdateBody) => {
            const response =
                await financialDocumentContractClient.updateAccountInfo({
                    params: { financialDocumentId },
                    body,
                });

            if (response.status !== 200) {
                throw new Error("Failed to update account info");
            }

            return response.body;
        },
        onMutate: (body) => onMutate?.(body),
        onSuccess: (updatedExtraction) => {
            onSuccess?.(updatedExtraction);

            updateFinancialDocumentDetailCaches(
                queryClient,
                financialDocumentId,
                (existingDocument) =>
                    existingDocument
                        ? {
                              ...existingDocument,
                              accountInfoExtraction: updatedExtraction,
                          }
                        : existingDocument,
            );
        },
        onError: (_error, _variables, context) => {
            onError?.(context);
        },
    });
}

export function useDownloadDocumentsMutation(onSuccess?: () => void) {
    return useMutation({
        mutationFn: async (
            body: ServerInferRequest<
                typeof financialDocumentContract.download
            >["body"],
        ) => {
            await financialDocumentContractClient.download({ body });
        },
        onSuccess,
    });
}

type BulkActionError =
    | RemovingDocumentWithTransactionsError
    | RemovingDocumentUsedInReconciliationError
    | InvalidDocumentCategoryError
    | LinkedDocumentFieldUpdateError;

type BulkActionBody = ServerInferRequest<
    typeof financialDocumentContract.bulkAction
>["body"];

interface UseBulkActionMutationOptions {
    onError?: (
        error: Error | BulkActionError,
        action: BulkActionBody["action"],
        payload: BulkActionBody,
    ) => void;
}

export function useFinancialDocumentBulkActionMutation(
    options: UseBulkActionMutationOptions = {},
) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (body: BulkActionBody) => {
            await financialDocumentContractClient.bulkAction({ body });
        },
        onError: (error, variables) => {
            if (
                isAxiosError(error) &&
                error.response?.status === 400 &&
                error.response?.data
            ) {
                options.onError?.(
                    error.response.data as BulkActionError,
                    variables.action,
                    variables,
                );
            } else {
                options.onError?.(error, variables.action, variables);
            }
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: financialDocumentQueryKeys.all(),
            });
        },
    });
}

export function useRunTaxonomyExtractionMutation() {
    const queryClient = useQueryClient();
    const { activeWorkspaceKey } = useScope();
    const { error: toastError } = useToaster();

    return useMutation({
        mutationFn: (financialDocumentId: number) =>
            runTaxonomyExtraction(financialDocumentId),
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey:
                    tasksQueryKeys.detailByWorkspaceKey(activeWorkspaceKey),
                exact: false,
            });
        },
        onError: () => {
            toastError("Failed to retry document extraction");
        },
    });
}

export function useUpdateTaxonomyExtractionFieldsMutation(
    financialDocumentId: number,
) {
    const queryClient = useQueryClient();
    const { activeWorkspaceKey } = useScope();
    const { error: toastError } = useToaster();

    return useMutation({
        mutationFn: async (body: DocumentTaxonomyExtraction) => {
            const result =
                await financialDocumentContractClient.updateTaxonomyExtraction({
                    body,
                    params: { financialDocumentId },
                });
            return result.body;
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey:
                    tasksQueryKeys.detailByWorkspaceKey(activeWorkspaceKey),
                exact: false,
            });
        },
        onError: () => {
            toastError("Failed to update document fields");
        },
    });
}

export async function updateBalanceSheetExtractionDetails({
    financialDocumentId,
    date,
    warnings,
}: {
    financialDocumentId: number;
    date?: string | null;
    warnings?: string[] | null;
}) {
    const result =
        await financialDocumentContractClient.updateBalanceSheetExtractionDetails(
            {
                params: { financialDocumentId },
                body: { date, warnings },
            },
        );

    if (result.status !== 200) {
        throw new Error("Failed to update balance sheet details");
    }
}

export async function batchUpdateInvoiceExtractionRows({
    financialDocumentId,
    invoiceExtractionId,
    upserts,
    deletes,
}: {
    financialDocumentId: number;
    invoiceExtractionId: string;
    upserts: Array<AccrualExtractionLineItem & { rowIndex: number }>;
    deletes: number[];
}) {
    await executeAccrualExtractionRequest(
        () =>
            financialDocumentContractClient.batchUpdateInvoiceExtractionRows({
                params: { financialDocumentId, invoiceExtractionId },
                body: { upserts, deletes },
            }),
        "Failed to batch update invoice extraction rows",
    );
}

export async function updateInvoiceExtractionDetails({
    financialDocumentId,
    invoiceExtractionId,
    counterpartyName,
    counterpartyId,
    documentNumber,
    documentDate,
    dueDate,
    totalAmount,
    memo,
    entityId,
    warnings,
    taxAmount,
    acceptSuggestion,
}: {
    financialDocumentId: number;
    invoiceExtractionId: string;
    counterpartyName?: string | null;
    counterpartyId?: string | null;
    documentNumber?: string | null;
    documentDate?: string | null;
    dueDate?: string | null;
    totalAmount?: number | null;
    memo?: string | null;
    entityId?: number | null;
    warnings?: string[] | null;
    taxAmount?: number | null;
    acceptSuggestion?: true;
}) {
    await executeAccrualExtractionRequest(
        () =>
            financialDocumentContractClient.updateInvoiceExtractionDetails({
                params: { financialDocumentId, invoiceExtractionId },
                body: {
                    counterpartyName,
                    counterpartyId,
                    documentNumber,
                    documentDate,
                    dueDate,
                    totalAmount,
                    memo,
                    entityId,
                    warnings,
                    taxAmount,
                    acceptSuggestion,
                },
            }),
        "Failed to update invoice extraction details",
    );
}

export async function batchUpdateBillExtractionRows({
    financialDocumentId,
    billExtractionId,
    upserts,
    deletes,
}: {
    financialDocumentId: number;
    billExtractionId: string;
    upserts: Array<AccrualExtractionLineItem & { rowIndex: number }>;
    deletes: number[];
}) {
    await executeAccrualExtractionRequest(
        () =>
            financialDocumentContractClient.batchUpdateBillExtractionRows({
                params: { financialDocumentId, billExtractionId },
                body: { upserts, deletes },
            }),
        "Failed to batch update bill extraction rows",
    );
}

export async function updateBillExtractionDetails({
    financialDocumentId,
    billExtractionId,
    counterpartyName,
    counterpartyId,
    documentNumber,
    documentDate,
    dueDate,
    totalAmount,
    memo,
    entityId,
    warnings,
    balanceDue,
    taxAmount,
    paymentDate,
    acceptSuggestion,
}: {
    financialDocumentId: number;
    billExtractionId: string;
    counterpartyName?: string | null;
    counterpartyId?: string | null;
    documentNumber?: string | null;
    documentDate?: string | null;
    dueDate?: string | null;
    totalAmount?: number | null;
    memo?: string | null;
    entityId?: number | null;
    warnings?: string[] | null;
    balanceDue?: number | null;
    taxAmount?: number | null;
    paymentDate?: string | null;
    acceptSuggestion?: true;
}) {
    await executeAccrualExtractionRequest(
        () =>
            financialDocumentContractClient.updateBillExtractionDetails({
                params: { financialDocumentId, billExtractionId },
                body: {
                    counterpartyName,
                    counterpartyId,
                    documentNumber,
                    documentDate,
                    dueDate,
                    totalAmount,
                    memo,
                    entityId,
                    warnings,
                    balanceDue,
                    taxAmount,
                    paymentDate,
                    acceptSuggestion,
                },
            }),
        "Failed to update bill extraction details",
    );
}

export async function updateInvoiceExtractions({
    financialDocumentId,
    invoiceExtractions,
}: {
    financialDocumentId: number;
    invoiceExtractions: InvoiceExtractionRecord[];
}) {
    try {
        const result =
            await financialDocumentContractClient.updateInvoiceExtractions({
                params: { financialDocumentId },
                body: { invoiceExtractions },
            });

        if (result.status !== 200) {
            throw new Error("Failed to update invoice extractions");
        }
    } catch (error) {
        if (isAxiosError(error) && error.response?.status === 400) {
            const message = error.response.data?.message;
            if (typeof message === "string") {
                throw new Error(message);
            }
        }
        throw error;
    }
}

export async function updateBillExtractions({
    financialDocumentId,
    billExtractions,
}: {
    financialDocumentId: number;
    billExtractions: BillExtractionRecord[];
}) {
    try {
        const result =
            await financialDocumentContractClient.updateBillExtractions({
                params: { financialDocumentId },
                body: { billExtractions },
            });

        if (result.status !== 200) {
            throw new Error("Failed to update bill extractions");
        }
    } catch (error) {
        if (isAxiosError(error) && error.response?.status === 400) {
            const message = error.response.data?.message;
            if (typeof message === "string") {
                throw new Error(message);
            }
        }
        throw error;
    }
}

export async function batchUpdateBalanceSheetExtractionRows({
    financialDocumentId,
    upserts,
    deletes,
}: {
    financialDocumentId: number;
    upserts: Array<BalanceSheetAccount & { rowIndex: number }>;
    deletes: number[];
}) {
    const result =
        await financialDocumentContractClient.batchUpdateBalanceSheetExtractionRows(
            {
                params: { financialDocumentId },
                body: { upserts, deletes },
            },
        );

    if (result.status !== 200) {
        throw new Error("Failed to batch update balance sheet rows");
    }
}

interface UpdateDocumentCounterpartyOptions {
    onSuccess?: (updatedDocument: FinancialDocumentExtended) => void;
}

export function useUpdateDocumentCounterpartyMutation(
    options: UpdateDocumentCounterpartyOptions = {},
) {
    const queryClient = useQueryClient();
    const { error: toastError } = useToaster();

    return useMutation({
        mutationFn: async ({
            financialDocumentId,
            counterpartyId,
        }: {
            financialDocumentId: number;
            counterpartyId: string | null;
        }) => {
            const result =
                await financialDocumentContractClient.updateDocumentCounterparty(
                    {
                        params: { financialDocumentId },
                        body: { counterpartyId },
                    },
                );

            if (result.status !== 200) {
                throw new Error("Failed to update document counterparty");
            }

            return FinancialDocumentExtendedDto.deserialize(result.body);
        },
        onSuccess: (updatedDocument) => {
            updateDocumentInCaches(updatedDocument, queryClient);
            queryClient.invalidateQueries({
                queryKey: financialDocumentQueryKeys.lists(),
            });
            options.onSuccess?.(updatedDocument);
        },
        onError: () => {
            toastError("Failed to update document counterparty");
        },
    });
}

export function useUpdateDocumentCategoryForTaskMutation(
    financialDocumentId: number,
) {
    const queryClient = useQueryClient();
    const { activeWorkspaceKey } = useScope();
    const { error: toastError } = useToaster();

    return useMutation({
        mutationFn: async (documentCategoryId: string | null) => {
            const result =
                await financialDocumentContractClient.updateDocumentCategory({
                    body: { documentCategoryId },
                    params: { financialDocumentId },
                });
            return result.body;
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey:
                    tasksQueryKeys.detailByWorkspaceKey(activeWorkspaceKey),
                exact: false,
            });
        },
        onError: () => {
            toastError("Failed to update document category");
        },
    });
}
